// Jun-2026 — PMC-A: Project Material Consumption UI (SEPARATE from Issue
// Voucher). ConsumeModal posts immediately (Option A); ConsumptionHistory
// shows the date-wise register with whole-document reversal.
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, RefreshCw, FileSpreadsheet, Undo2, Eye, Camera } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { EvidencePhotos } from "@/components/EvidencePhotos";
import { SearchableSelect } from "@/components/SearchableSelect";

const PMC_PHOTO_FROZEN = ["Reversing", "Reversed", "Failed — Balance Changed"];

const inputCls = "border border-slate-300 rounded px-2 py-1.5 text-xs w-full";
const today = () => new Date().toLocaleDateString("en-CA");

export function ConsumeModal({ project, row, onClose, onPosted }) {
  const [form, setForm] = useState({
    consumption_date: today(), consumed_qty: "", line_remark: "",
    work_location: "", boq_element_code: "", backdate_reason: "",
    period_exception_reason: "",
  });
  const [boq, setBoq] = useState([]);
  const [busy, setBusy] = useState(false);
  const [needPeriodReason, setNeedPeriodReason] = useState(false);

  useEffect(() => {
    api.get(`/projects/${project.id}/consumable-boq-elements`)
      .then((r) => setBoq(r.data?.elements || [])).catch(() => {});
  }, [project.id]);

  const backdated = form.consumption_date && form.consumption_date < today();
  const pStart = (project?.project_start_date || "").slice(0, 10);
  const pEnd = (project?.project_end_date || "").slice(0, 10);
  const outsidePeriod = needPeriodReason || (!!form.consumption_date &&
    ((pStart && form.consumption_date < pStart) || (pEnd && form.consumption_date > pEnd)));

  const submit = async () => {
    if (!form.consumed_qty || Number(form.consumed_qty) <= 0)
      return toast.error("Enter a positive quantity");
    if (!form.line_remark.trim()) return toast.error("Usage remark is mandatory");
    if (!form.work_location.trim()) return toast.error("Work location is mandatory");
    if (outsidePeriod && !form.period_exception_reason.trim())
      return toast.error("Exception reason is mandatory — the date is outside the project period");
    setBusy(true);
    try {
      const r = await api.post(`/projects/${project.id}/consumptions`, {
        consumption_date: form.consumption_date,
        work_location: form.work_location.trim(),
        backdate_reason: form.backdate_reason.trim(),
        period_exception_reason: form.period_exception_reason.trim(),
        line_items: [{
          batch_id: row.batch_id, consumed_qty: Number(form.consumed_qty),
          uom: row.uom, line_remark: form.line_remark.trim(),
          boq_element_code: form.boq_element_code || "",
        }],
      });
      toast.success(`Consumption ${r.data.consumption_number} posted`);
      onPosted?.(); onClose();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to post consumption";
      if (String(msg).toLowerCase().includes("outside the project period")) setNeedPeriodReason(true);
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto py-10" data-testid="consume-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div className="text-sm font-bold text-slate-900">Consume — {row.item_name}</div>
          <button data-testid="consume-modal-close" onClick={onClose} className="text-slate-500 hover:text-slate-800"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3 text-xs">
          <div className="bg-slate-50 border border-slate-200 rounded p-2 font-mono" data-testid="consume-batch-info">
            {row.batch_code} · Available <b>{row.available_qty} {row.uom}</b>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="space-y-1"><span className="font-bold text-slate-600">Consumption Date *</span>
              <input data-testid="consume-date-input" type="date" max={today()} className={inputCls}
                value={form.consumption_date} onChange={(e) => setForm({ ...form, consumption_date: e.target.value })} />
            </label>
            <label className="space-y-1"><span className="font-bold text-slate-600">Quantity ({row.uom}) *</span>
              <input data-testid="consume-qty-input" type="number" min="0" step="any" className={inputCls}
                value={form.consumed_qty} onChange={(e) => setForm({ ...form, consumed_qty: e.target.value })} />
            </label>
          </div>
          <label className="block space-y-1"><span className="font-bold text-slate-600">Work Location *</span>
            <input data-testid="consume-location-input" className={inputCls} placeholder="e.g. Block A — Level 2 slab"
              value={form.work_location} onChange={(e) => setForm({ ...form, work_location: e.target.value })} />
          </label>
          <label className="block space-y-1"><span className="font-bold text-slate-600">Usage Remark *</span>
            <textarea data-testid="consume-remark-input" className={`${inputCls} h-16`} placeholder="What was the material used for?"
              value={form.line_remark} onChange={(e) => setForm({ ...form, line_remark: e.target.value })} />
          </label>
          <label className="block space-y-1"><span className="font-bold text-slate-600">BOQ Element (optional) <span className="font-normal text-slate-400">— searchable</span></span>
            <SearchableSelect testid="consume-boq-select" className="w-full"
              items={boq} value={form.boq_element_code}
              onChange={(code) => setForm({ ...form, boq_element_code: code || "" })}
              getKey={(el) => el.element_code}
              getLabel={(el) => `${el.element_code} — ${(el.description || "").slice(0, 60)}`}
              getSubLabel={(el) => el.category_display || ""}
              getSearchText={(el) => `${el.element_code} ${el.description || ""} ${el.category_display || ""}`}
              placeholder="— none —" searchPlaceholder="Search BOQ element by code / description…" />
          </label>
          {backdated && (
            <label className="block space-y-1"><span className="font-bold text-amber-700">Backdating Reason * (requires PMC backdating permission)</span>
              <input data-testid="consume-backdate-reason" className={inputCls}
                value={form.backdate_reason} onChange={(e) => setForm({ ...form, backdate_reason: e.target.value })} />
            </label>
          )}
          {outsidePeriod && (
            <div className="border border-amber-300 bg-amber-50 rounded p-3 space-y-1.5" data-testid="consume-period-exception-box">
              <div className="text-amber-800 font-bold">
                {form.consumption_date} is outside the project period ({pStart || "—"} → {pEnd || "—"}). An explicit exception reason is required to post.
              </div>
              <label className="block space-y-1"><span className="font-bold text-amber-700">Period Exception Reason *</span>
                <input data-testid="consume-period-reason" className={inputCls}
                  value={form.period_exception_reason}
                  onChange={(e) => setForm({ ...form, period_exception_reason: e.target.value })}
                  placeholder="Why is consumption recorded outside the project period?" />
              </label>
            </div>
          )}
          <div className="text-[10px] text-slate-400">
            Posts immediately and reduces the locked Project Stock balance. Separate from Issue Voucher — no IV is created.
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="px-3 py-1.5 border rounded">Cancel</button>
            <button data-testid="consume-submit-btn" disabled={busy} onClick={submit}
              className="px-4 py-1.5 bg-emerald-700 text-white font-bold rounded disabled:opacity-50">
              {busy ? "Posting…" : "Post Consumption"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// UX-1 — append-only site photographs for one PMC document (max 6)
export function PmcPhotosModal({ project, doc, onClose, onChanged }) {
  const { hasPerm } = useAuth();
  const frozen = PMC_PHOTO_FROZEN.includes(doc.status);
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center py-16" data-testid="pmc-photos-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 p-5 space-y-3 text-xs">
        <div className="flex items-center justify-between">
          <div className="text-sm font-bold text-slate-900">Site Photographs — {doc.consumption_number}</div>
          <button data-testid="pmc-photos-close" onClick={onClose} className="text-slate-500 hover:text-slate-800"><X size={16} /></button>
        </div>
        {frozen && (
          <div className="text-[11px] text-orange-800 bg-orange-50 border border-orange-200 rounded px-2 py-1" data-testid="pmc-photos-frozen-note">
            Document is {doc.status} — new uploads are frozen; existing photos remain visible as historical evidence.
          </div>
        )}
        <EvidencePhotos title="Site Photographs" photos={doc.photos || []} max={6}
          canUpload={!frozen && hasPerm("project_material_consumption", "upload")}
          uploadUrl={`/projects/${project.id}/consumptions/${doc.id}/photos`}
          photoUrl={(id) => `/projects/${project.id}/consumptions/${doc.id}/photos/${id}`}
          onChanged={onChanged} testidPrefix="pmc-photos" />
      </div>
    </div>
  );
}

function ReverseModal({ project, doc, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!reason.trim()) return toast.error("Reversal reason is mandatory");
    setBusy(true);
    try {
      await api.post(`/projects/${project.id}/consumptions/${doc.id}/reverse`, { reason: reason.trim() });
      toast.success(`Consumption ${doc.consumption_number} reversed — stock restored`);
      onDone?.(); onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reversal failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center py-16" data-testid="pmc-reverse-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-5 space-y-3 text-xs">
        <div className="text-sm font-bold text-slate-900">Reverse {doc.consumption_number}?</div>
        <div className="text-slate-500">Whole-document reversal: every line quantity is restored to its batch. The document and its history are retained.</div>
        <textarea data-testid="pmc-reverse-reason" className={`${inputCls} h-16`} placeholder="Reason (mandatory)"
          value={reason} onChange={(e) => setReason(e.target.value)} />
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 border rounded">Cancel</button>
          <button data-testid="pmc-reverse-confirm" disabled={busy} onClick={submit}
            className="px-4 py-1.5 bg-orange-600 text-white font-bold rounded disabled:opacity-50">
            {busy ? "Reversing…" : "Reverse"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function ConsumptionHistory({ project, canConsume, onDrill }) {
  const pid = project.id;
  const [data, setData] = useState({ rows: [], total: 0, values_visible: false });
  const [page, setPage] = useState(1);
  const [flt, setFlt] = useState({ q: "", date_from: "", date_to: "" });
  const [rev, setRev] = useState(null);
  const [photosFor, setPhotosFor] = useState(null);
  const limit = 25;

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/projects/${pid}/consumptions`, { params: {
        page, limit, q: flt.q || undefined,
        date_from: flt.date_from || undefined, date_to: flt.date_to || undefined } });
      setData(r.data || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load consumption history");
    }
  }, [pid, page, flt]);
  useEffect(() => { load(); }, [load]);

  const doExport = async () => {
    try {
      const r = await api.get(`/projects/${pid}/consumptions-export`, {
        responseType: "blob",
        params: { date_from: flt.date_from || undefined, date_to: flt.date_to || undefined } });
      const url = URL.createObjectURL(r.data);
      const link = document.createElement("a");
      link.href = url; link.download = `PMC_${project.project_code}.xlsx`; link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Export failed (PMC export permission required)"); }
  };

  const vv = !!data.values_visible;
  return (
    <div className="space-y-3" data-testid="pmc-history">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <input data-testid="pmc-search-input" className="border border-slate-300 rounded px-2 py-1.5 text-xs w-full sm:w-52"
            placeholder="Search number / item / remark…" value={flt.q}
            onChange={(e) => { setFlt({ ...flt, q: e.target.value }); setPage(1); }} />
          <input data-testid="pmc-date-from" type="date" className="border border-slate-300 rounded px-2 py-1.5 text-xs"
            value={flt.date_from} onChange={(e) => { setFlt({ ...flt, date_from: e.target.value }); setPage(1); }} />
          <input data-testid="pmc-date-to" type="date" className="border border-slate-300 rounded px-2 py-1.5 text-xs"
            value={flt.date_to} onChange={(e) => { setFlt({ ...flt, date_to: e.target.value }); setPage(1); }} />
          <button data-testid="pmc-refresh-btn" onClick={load} className="p-1.5 text-slate-500 hover:text-slate-800"><RefreshCw size={14} /></button>
        </div>
        <button data-testid="pmc-export-btn" onClick={doExport}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold text-emerald-800 border border-emerald-300 rounded hover:bg-emerald-50">
          <FileSpreadsheet size={13} /> Export Excel
        </button>
      </div>
      <div className="hidden md:block border border-slate-200 rounded overflow-x-auto">
        <table className="w-full text-xs" data-testid="pmc-history-table">
          <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2 text-left">Number</th>
              <th className="px-3 py-2 text-left">Date</th>
              <th className="px-3 py-2 text-left">Item / Batch</th>
              <th className="px-3 py-2 text-right">Qty</th>
              {vv && <th className="px-3 py-2 text-right">Prov. Value</th>}
              <th className="px-3 py-2 text-left">BOQ</th>
              <th className="px-3 py-2 text-left">Work Location</th>
              <th className="px-3 py-2 text-left">Remark</th>
              <th className="px-3 py-2 text-left">Entered By</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {(data.rows || []).length === 0 && (
              <tr><td colSpan={11} className="px-3 py-8 text-center text-slate-400" data-testid="pmc-history-empty">
                No consumption recorded yet.
              </td></tr>
            )}
            {(data.rows || []).map((d) => (d.line_items || []).map((l, li) => (
              <tr key={`${d.id}-${li}`} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`pmc-row-${d.consumption_number}`}>
                <td className="px-3 py-2 font-mono">{li === 0 ? d.consumption_number : ""}</td>
                <td className="px-3 py-2">{li === 0 ? d.consumption_date : ""}</td>
                <td className="px-3 py-2">{l.item_name}<div className="text-slate-400 font-mono">{l.batch_code}</div></td>
                <td className="px-3 py-2 text-right font-bold">{l.consumed_qty} {l.uom}</td>
                {vv && <td className="px-3 py-2 text-right">{l.provisional_value}</td>}
                <td className="px-3 py-2">{l.boq_element_code || "—"}</td>
                <td className="px-3 py-2">{d.work_location}</td>
                <td className="px-3 py-2 max-w-[180px] truncate" title={l.line_remark}>{l.line_remark}</td>
                <td className="px-3 py-2">{d.entered_by}</td>
                <td className="px-3 py-2">
                  {d.status === "Reversed"
                    ? <span className="inline-flex items-center gap-1 text-orange-700 font-bold" title={d.reversal_reason}><Undo2 size={11} /> Reversed</span>
                    : <span className="text-emerald-700 font-bold">{d.status}</span>}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {li === 0 && (
                    <button data-testid={`pmc-photos-${d.consumption_number}`} onClick={() => setPhotosFor(d)}
                      className="text-slate-700 hover:underline inline-flex items-center gap-1 mr-2">
                      <Camera size={12} /> Photos{(d.photos || []).length > 0 ? ` (${(d.photos || []).length})` : ""}
                    </button>
                  )}
                  {li === 0 && (
                    <button data-testid={`pmc-drill-${d.consumption_number}`} onClick={() => onDrill?.(l)}
                      className="text-sky-700 hover:underline inline-flex items-center gap-1 mr-2"><Eye size={12} /> Batch</button>
                  )}
                  {li === 0 && canConsume && d.status === "Posted" && (
                    <button data-testid={`pmc-reverse-${d.consumption_number}`} onClick={() => setRev(d)}
                      className="text-orange-700 hover:underline inline-flex items-center gap-1"><Undo2 size={12} /> Reverse</button>
                  )}
                </td>
              </tr>
            )))}
          </tbody>
        </table>
      </div>

      {/* Mobile: stacked consumption cards (one per document) */}
      <div className="md:hidden border border-slate-200 rounded divide-y divide-slate-100" data-testid="pmc-history-cards">
        {(data.rows || []).length === 0 && (
          <div className="px-3 py-6 text-center text-slate-400 text-xs">No consumption recorded yet.</div>
        )}
        {(data.rows || []).map((d) => (
          <div key={d.id} className="p-3 space-y-1.5" data-testid={`pmc-card-${d.consumption_number}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-[11px] font-bold">{d.consumption_number}</span>
              {d.status === "Reversed"
                ? <span className="inline-flex items-center gap-1 text-orange-700 font-bold text-[10px]" title={d.reversal_reason}><Undo2 size={10} /> Reversed</span>
                : <span className="text-emerald-700 font-bold text-[10px]">{d.status}</span>}
              <span className="ml-auto text-[10px] text-slate-400">{d.consumption_date}</span>
            </div>
            {(d.line_items || []).map((l, li) => (
              <div key={li} className="text-[11px] border-l-2 border-slate-200 pl-2">
                <div className="text-slate-800 break-words">{l.item_name} <span className="font-mono text-[10px] text-slate-400">{l.batch_code}</span></div>
                <div className="text-slate-500 flex flex-wrap gap-x-3">
                  <span>Qty: <b className="text-slate-800">{l.consumed_qty} {l.uom}</b></span>
                  {vv && l.provisional_value != null && <span>Value: {l.provisional_value}</span>}
                  {l.boq_element_code && <span>BOQ: {l.boq_element_code}</span>}
                </div>
                {l.line_remark && <div className="text-[10px] text-slate-400 break-words">{l.line_remark}</div>}
              </div>
            ))}
            <div className="text-[10px] text-slate-500 break-all">{d.work_location ? `${d.work_location} · ` : ""}{d.entered_by}</div>
            <div className="flex flex-wrap items-center gap-3 pt-0.5">
              <button data-testid={`pmc-photos-m-${d.consumption_number}`} onClick={() => setPhotosFor(d)}
                className="text-slate-700 hover:underline inline-flex items-center gap-1 text-xs">
                <Camera size={12} /> Photos{(d.photos || []).length > 0 ? ` (${(d.photos || []).length})` : ""}
              </button>
              <button data-testid={`pmc-drill-m-${d.consumption_number}`} onClick={() => onDrill?.((d.line_items || [])[0])}
                className="text-sky-700 hover:underline inline-flex items-center gap-1 text-xs"><Eye size={12} /> Batch</button>
              {canConsume && d.status === "Posted" && (
                <button data-testid={`pmc-reverse-m-${d.consumption_number}`} onClick={() => setRev(d)}
                  className="text-orange-700 hover:underline inline-flex items-center gap-1 text-xs"><Undo2 size={12} /> Reverse</button>
              )}
            </div>
          </div>
        ))}
      </div>
      {data.total > limit && (
        <div className="flex items-center justify-end gap-2 text-xs">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="px-2 py-1 border rounded disabled:opacity-40">Prev</button>
          <span>Page {page} / {Math.ceil(data.total / limit)}</span>
          <button disabled={page >= Math.ceil(data.total / limit)} onClick={() => setPage(page + 1)} className="px-2 py-1 border rounded disabled:opacity-40">Next</button>
        </div>
      )}
      {rev && <ReverseModal project={project} doc={rev} onClose={() => setRev(null)} onDone={load} />}
      {photosFor && <PmcPhotosModal project={project} doc={photosFor}
        onClose={() => setPhotosFor(null)} onChanged={load} />}
    </div>
  );
}
